<template>
  <div id="api-docs">
    <section class="hero is-medium is-primary is-bold">
      <div class="hero-body">
        <div class="container">
          <h1 class="title">
            ShapeStore&trade;
          </h1>
          <h2 class="subtitle">
            API Documentation
          </h2>
        </div>
      </div>
    </section>

    <div class="tile is-ancestor">
      <div class="tile is-3 is-parent">
        <div class="tile is-child box">
          <aside class="menu">
            <p v-for="(item, itemIndex) in apiRoutes"
              v-bind:key="itemIndex"
              class="menu-label">
              <a v-on:click="itemClick(getItemId(itemIndex))"
                :id="'item_' + itemIndex"
                v-bind:class="{ 'is-active': activeItem === getItemId(itemIndex) }">
                <span v-if="activeItem !== getItemId(itemIndex)"
                  class="item-arrow">
                  <font-awesome-icon icon="angle-right"/>
                </span>
                <span v-if="activeItem === getItemId(itemIndex)"
                  class="item-arrow-active">
                  <font-awesome-icon icon="angle-down" />
                </span>
                {{ item.name }}
              </a>

              <ul class="menu-list"
                v-if="activeItem === getItemId(itemIndex)">
                <li v-for="(route, routeIndex) in item.item"
                  v-bind:key="routeIndex">
                  <a v-on:click="routeClick(route, getRouteId(itemIndex, routeIndex))"
                    :id="'route_' + itemIndex + '_' + routeIndex"
                    v-bind:class="{
                      'is-active': activeRoute === getRouteId(itemIndex, routeIndex)
                    }">
                    <span
                      v-bind:class="[ route.request.method === 'GET' ? 'get' : 'post' ]"
                      >{{ route.request.method }}</span> {{ route.name }}
                  </a>
                </li>
              </ul>
            </p>
          </aside>
        </div>
      </div>

      <div id="route-examples"
        class="tile is-parent">
        <div v-if="currentRoute !== null"
          class="current-route tile is-child box"
          v-bind:class="{ 'is-visible': showCurrentRoute }">
          <p class="item-row">
            <span class="item-label">API URL</span>: https://api.shapelog.com
          </p>
          <p class="route-path">
            <!-- eslint-disable-next-line -->
            <pre><strong>Route</strong>: {{ currentRoute.request.url.raw.replace('\{\{url\}\}', '') }}</pre>
          </p>
          <div class="headers-info">
            <h1>Request Headers:</h1>
            <div class="header-types">
              <p v-for="(header, index) in currentRoute.request.header"
                v-bind:key="index">
                <span class="header-label">{{ header.key }}</span>: {{ header.value }}
              </p>
            </div>
          </div>

          <div v-for="(response, index) in currentRoute.response"
            v-bind:key="index"
            class="example-code">

            <div v-if="response.originalRequest.body && response.originalRequest.body.raw"
              class="example-request">
              <h1><span class="menu-label">REQUEST</span>: {{ response.name }}:</h1>
              <Prism language="javascript">
{{ response.originalRequest.body.raw }}
              </Prism>
            </div>

            <div v-if="!response.originalRequest.body || !response.originalRequest.body.raw"
              class="example-request">
              <h1><span class="menu-label">REQUEST</span>: {{ response.name }}:</h1>
              <Prism language="javascript">
None
              </Prism>
            </div>

            <div class="example-response">
              <h1><span class="menu-label">RESPONSE</span>: {{ response.name }}:</h1>
              <Prism language="javascript">
{{ response.body }}
              </Prism>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Prism from 'vue-prism-component';

export default {
  name: 'api-docs',
  components: {
    Prism,
  },
  mounted() {
    this.fixPrismNumbers();
    this.$store.dispatch('getCollection');
  },
  data() {
    return {
      baseUrl: 'https://api.shapelog.com',
      activeItem: null,
      activeRoute: null,
    };
  },
  computed: {
    apiRoutes() {
      return this.$store.state.apiRoutes;
    },
    currentRoute() {
      return this.$store.state.currentRoute;
    },
    currentRouteRequestBody() {
      if (this.currentRoute.request.body) {
        if (this.currentRoute.request.body.raw) {
          return this.currentRoute.request.body.raw;
        }
      }
      return 'None';
    },
    showCurrentRoute() {
      // eslint-disable-next-line
      if (this.currentRoute.id) {
        return true;
      }
      return false;
    },
  },
  methods: {
    getItemId(itemIndex) {
      const itemId = `item_${itemIndex}`;
      return itemId;
    },
    getRouteId(itemIndex, routeIndex) {
      const routeId = `route_${itemIndex}_${routeIndex}`;
      return routeId;
    },
    itemClick(itemId) {
      this.activeItem = itemId;
    },
    routeClick(route, routeId) {
      this.activeRoute = routeId;
      this.$store.commit('setCurrentRoute', route);
    },
    fixPrismNumbers() {
      /*
        NOTE: This is a workaround for the fact that BOTH bulma and
        prismjs define the same `.number` CSS class. Rather than
        modify the source code for either, this method replaces any
        instances of that single class with a "safe" version.

        The "safe" version is based on the Okaidia PrimsJS theme.
      */
      const allNumbers = document.getElementsByClassName('number');
      if (allNumbers.length > 0) {
        // eslint-disable-next-line
        for (let i=(allNumbers.length - 1);i >= 0; i--) {
          const el = allNumbers[i];
          el.classList.add('prism-number');
          el.classList.remove('number');
        }
      }
    },
  },
  watch: {
    currentRoute() {
      // Give the DOM a few milliseconds to render...
      setTimeout(() => { this.fixPrismNumbers(); }, 0);
    },
  },
};
</script>

<style scoped lang="scss">
  @import '@/styles/prism-okaidia.scss';
  @import '@/styles/common.scss';

  .hero.is-primary {
    background-color: $shapelog-orange;
  }

  .hero-body {
    background-image: url('../assets/shapestore_wave.png');
    background-position: center;
    background-size: cover;
    background-repeat: no-repeat;
  }

  #api-docs {
    height: 95vh;
    overflow: scroll;
  }

  .current-route {
    display: none;
  }

  .is-visible {
    display: block;
  }
  .prism-number {
    color: #ae81ff !important;
  }

  .get {
    color: $shapelog-green;
    font-weight: $font-weight-semibold;
  }
  .post {
    color: $shapelog-orange;
    font-weight: $font-weight-semibold;
  }
  .patch {
    color: $shapelog-medium-gray;
    font-weight: $font-weight-semibold;
  }
  .del {
    color: $shapelog-red;
    font-weight: $font-weight-semibold;
  }

  .route-path {
    background: #f4f4f4;
    line-height: 24px;
    border: 1px solid #d4d4d4;
    color: #282828;
    border-radius: 3px;
    font-size: 14px;
    padding: 4px 8px;
    word-break: break-all;
    width: 100%;

    pre {
      padding: 0;
    }
  }

  .headers-info {
    color: #282828;
    font-size: 14px;
    padding: 4px 8px;
    width: 100%;
    margin-top: 10px;
    border: 1px solid #d4d4d4;
    border-radius: 3px;
    margin-bottom: 20px;

    h1 {
      font-weight: $font-weight-semibold;
    }

    .header-types {

      .header-label {
        font-weight: $font-weight-semibold;
      }
    }
  }

  .item-row {
    color: #282828;
    font-size: 14px;
    padding: 4px 8px;
    width: 100%;
    margin-top: 10px;
    border: 1px solid #d4d4d4;
    border-radius: 3px;
    margin-bottom: 20px;

    .item-label {
      font-weight: $font-weight-semibold;
    }
  }

  .tile {

    &.is-ancestor {
      margin: 30px;
    }

    &.is-child {
      &.box {

      }
    }

    .menu {
      .menu-label {
        font-size: 16px;
        margin-bottom: 0;
        font-weight: $font-weight-semibold;
        color: $shapelog-dark-gray;
      }

      .menu-list {
        li {
          a {
            font-size: 12px;
            &.is-active {
              background-color: #333333;
            }
          }
        }
      }
    }
  }

  .menu-label {
    background-color: #f4f4f4;
    border-radius: 2px;
    padding-left: 5px;
    padding-right: 5px;

    &:not(:first-child) {
      margin-top: 2px;
    }
    a {
      font-size: 14px;
      color: $shapelog-medium-gray;
    }
  }

  .item-arrow {
    color: $shapelog-blue;
  }

  .item-arrow-active {
    color: $shapelog-orange;
  }

</style>
